// Boiler Plate Imports
import React, { useEffect, useMemo, useState } from "react";
import colors from "../../theme/colors";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import {
  remove as removeNotification,
  add as addNotification,
} from "../../redux-store/notificationsSlice";
import { update as setBreadCrumbs } from "../../redux-store/breadCrumbsSlice";
import API from "../../api";

// Conatiner Component Imports
import { Button, Card } from "@mui/material";
import BreadCrumbs from "../../components/Navigation/breadcrumbs";
import ModalComponent from "../../components/Modal";
import TableHeader from "../../components/Page/tableHeader";

// Page Component Imports
import AdminOrganizationForm from "../../components/Forms/adminOrganizationForm";
import { FormatPhoneNumber } from "../../components/PhoneNumberInput";
import MetricBoxes from "../../components/Page/metricBoxes";
import { TableComponent, ItemLink } from "../../components/Table/dataGridPro";

const ActiveAlarmsTagNone = styled.div`
  background: ${colors.grayLight};
  color: ${colors.grayDark};
  border: 1px solid ${colors.gray};
  border-radius: 15px;
  text-align: center;
  padding: 2px;
  width: 60px;
`;

const ActiveAlarmsTagSome = styled.div`
  background: ${colors.redLightError};
  color: ${colors.redError};
  border: 1px solid ${colors.redError};
  border-radius: 15px;
  text-align: center;
  padding: 2px;
  width: 60px;
  font-weight: bold;
`;

const PaddingWrapper = styled.div`
  padding: 0 15px 10px;
  @media (max-width: 1080px) {
    padding: 0 0px 10px;
  }
`;

const Table = React.memo(TableComponent);

const AdminOrgsContainer = () => {
  const [savingOrganization, setSavingOrganization] = useState(false);
  const [savingOrganizationError, setSavingOrganizationError] = useState(false);

  const [reloadData, setReloadData] = useState(false);
  const [organizationMetrics, setOrganizationMetrics] = useState([]);

  const [openOrganizationModal, setOpenOrganizationModal] = useState(false);
  const dispatch = useDispatch();

  const tableColumns = useMemo(
    () => [
      {
        headerName: "Name",
        field: "name",
        valueGetter: (value, row) => {
          return row.name;
        },
        renderCell: ({ row }) => {
          return (
            <ItemLink to={`/${row.id}/organization/`}>{row.name}</ItemLink>
          );
        },
      },
      {
        headerName: "Active Alarms",
        field: "activeAlarmsCount",
        display: "flex",
        type: "number",
        valueGetter: (value, row) => {
          return row.activeAlarmsCount;
        },
        renderCell: ({ row }) => {
          if (row.activeAlarmsCount === 0) {
            return <ActiveAlarmsTagNone>none</ActiveAlarmsTagNone>;
          } else {
            return (
              <ActiveAlarmsTagSome>{row.activeAlarmsCount}</ActiveAlarmsTagSome>
            );
          }
        },
      },
      {
        headerName: "Total Locations",
        field: "locationsCount",
        type: "number",
      },
      {
        headerName: "Total Tanks",
        field: "tanksCount",
        type: "number",
      },
      {
        headerName: "Total Users",
        field: "usersCount",
        type: "number",
      },
      {
        headerName: "Date Created",
        field: "dateCreatedObject",
        type: "dateTime",
      },
    ],
    []
  );

  useEffect(() => {
    dispatch(
      setBreadCrumbs([
        {
          title: "Admin Organizations",
          link: "/admin/organizations",
        },
      ])
    );
    setOrganizationMetrics([
      {
        title: "ACTIVE ALARMS",
        value: "",
      },
      {
        title: "ORGANIZATIONS",
        value: "",
      },
      {
        title: "LOCATIONS",
        value: "",
      },
      {
        title: "ACTIVE TANKS",
        value: "",
      },
      {
        title: "USERS",
        value: "",
      },
    ]);
  }, []);

  const dataLayer = {
    GetAllOrganizations: async () => {
      const { organizations, metrics } =
        await API.Organization.OrganizationsGetAll();

      setOrganizationMetrics([
        {
          title: "ACTIVE ALARMS",
          value: metrics.activeAlarmsCount,
        },
        {
          title: "ORGANIZATIONS",
          value: metrics.organizationsCount,
        },
        {
          title: "LOCATIONS",
          value: metrics.locationsCount,
        },
        {
          title: "ACTIVE TANKS",
          value: metrics.tanksCount,
        },
        {
          title: "USERS",
          value: metrics.usersCount,
        },
      ]);

      const orgRows = organizations.map((org) => {
        return {
          ...org,
          status: (() => {
            if (org.deleted) {
              return "Deactivated";
            } else {
              return "Active";
            }
          })(),
          dateCreatedObject: new Date(org.created),
          phoneString: FormatPhoneNumber(org.phone),
        };
      });
      orgRows.sort((a, b) => {
        return b.name - a.name;
      });

      return orgRows;
    },
  };

  const handlers = {
    addOrganizationClick: () => {
      setOpenOrganizationModal(true);
    },
    closeOrganizationModal: () => {
      setOpenOrganizationModal(false);
    },
    saveOrganization: async (data) => {
      setSavingOrganizationError("");
      setSavingOrganization(true);
      try {
        await API.Organization.OrganizationCreate({
          createOrganizationData: { ...data },
        });
        //Reload Users
        setReloadData(true);
        //Create Success Notification
        setOpenOrganizationModal(false);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "success",
            title: "Success",
            message: "Organization created successfully",
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      } catch (err) {
        setSavingOrganizationError(err);
        console.log(err);
        const id = new Date().getTime();
        dispatch(
          addNotification({
            id,
            severity: "error",
            title: "Error",
            message: err,
          })
        );
        setTimeout(() => {
          dispatch(removeNotification(id));
        }, 3000);
      }
      setSavingOrganization(false);
    },
  };

  return (
    <>
      
      <MetricBoxes title={"Boreas Overview"} metrics={organizationMetrics} />
      <PaddingWrapper>
        <Card>
          <TableHeader>
            <h2>Organizations</h2>
            <Button
              onClick={handlers.addOrganizationClick}
              variant="contained"
              color="primary"
              size="large"
            >
              ADD ORGANIZATION
            </Button>
          </TableHeader>

          <Table
            columns={tableColumns}
            label={""}
            isAdminView={true}
            reloadData={reloadData}
            placeholder="There are no organizations created yet"
            getRowsFunction={dataLayer.GetAllOrganizations}
            initialState={{
              pinnedColumns: { left: ["name"] },
              sorting: {
                sortModel: [
                  { field: "activeAlarmsCount", sort: "desc" },
                  { field: "name", sort: "asc" },
                ],
              },
            }}
          />
        </Card>
      </PaddingWrapper>
      <ModalComponent
        headerText="Add New Organization"
        open={openOrganizationModal}
        handleClose={handlers.closeUserModal}
      >
        <AdminOrganizationForm
          handleSave={handlers.saveOrganization}
          handleCancel={handlers.closeOrganizationModal}
          loading={savingOrganization}
          error={savingOrganizationError}
        />
      </ModalComponent>
    </>
  );
};

export default AdminOrgsContainer;
